import { navigate } from "gatsby";

const DEBUG_MODE = false;
//Change to true to access profile locally

export const isBrowser = () => typeof window !== "undefined";

const SESSION_KEY = "mobile_backend_session";

const decodeJwtExpiry = (token) => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.exp;
  } catch {
    return 0;
  }
};

const setSession = (token) =>
  window.localStorage.setItem(
    SESSION_KEY,
    JSON.stringify({ token, expire: decodeJwtExpiry(token) }),
  );

export const getSession = () => {
  if (!isBrowser()) return false;
  const raw = window.localStorage.getItem(SESSION_KEY);
  if (!raw) return false;
  const session = JSON.parse(raw);
  return session.expire > ~~(Date.now() / 1000) ? session : false;
};

export const getSessionToken = () => {
  const session = getSession();
  return session ? session.token : null;
};

/**
 * True when a token is still in storage but its exp has passed. logout()
 * removes the key, so this stays false for a deliberate sign-out - only a
 * session that ran out of time is reported.
 */
export const isSessionExpired = () => {
  if (!isBrowser()) return false;
  const raw = window.localStorage.getItem(SESSION_KEY);
  if (!raw) return false;
  try {
    return JSON.parse(raw).expire <= ~~(Date.now() / 1000);
  } catch {
    // Unparseable session data is unusable either way.
    return true;
  }
};

const ACCOUNT_KEY = "mobile_backend_account";

const accountCacheValid = () => {
  if (!isBrowser()) return false;
  const raw = window.localStorage.getItem(ACCOUNT_KEY);
  if (!raw) return false;
  return JSON.parse(raw)._revised > ~~(Date.now() / 1000) - 5 * 60;
};

export const getAccount = async () => {
  if (!isBrowser()) return null;

  if (accountCacheValid()) {
    return JSON.parse(window.localStorage.getItem(ACCOUNT_KEY));
  }

  const token = getSessionToken();
  if (!token) return null;

  const response = await fetch(
    `${process.env.REACT_APP_MOBILE_BACKEND_ENDPOINT}/v2/auth/me`,
    { headers: { Authorization: `Bearer ${token}` } },
  );

  if (!response.ok) return null;

  const data = await response.json();
  if (data.status !== "ok") return null;

  const account = { ...data.user, _revised: ~~(Date.now() / 1000) };
  window.localStorage.setItem(ACCOUNT_KEY, JSON.stringify(account));
  return account;
};

export const handleLogin = async ({
  email,
  password,
  goto = "/app/profile",
}) => {
  if (DEBUG_MODE) {
    setSession("debug-token");
    navigate(goto);
    return { success: true };
  }

  const response = await fetch(
    `${process.env.REACT_APP_MOBILE_BACKEND_ENDPOINT}/v2/auth/manager-login`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ identifier: email, password }),
    },
  );

  const data = await response.json();

  if (!response.ok || data.status !== "ok") {
    return { error: data.message || "Login failed", status: response.status };
  }

  setSession(data.token);
  navigate(goto);
  return { success: true };
};

// Registers an account. The email must already be verified via the
// 4-digit-code flow in verification.mjs (handleVerification / complateVerification) -
// the backend rejects registration for an unverified email.
export const handleRegistration = async ({
  email,
  password,
  first_name,
  last_name,
  username,
  goto = "/app/profile",
}) => {
  if (DEBUG_MODE) {
    setSession("debug-token");
    navigate(goto);
    return { success: true };
  }

  const response = await fetch(
    `${process.env.REACT_APP_MOBILE_BACKEND_ENDPOINT}/v2/auth/register`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email,
        password,
        first_name,
        last_name,
        username: username || email,
      }),
    },
  );

  const data = await response.json();

  if (!response.ok || data.status !== "ok") {
    return {
      error: data.message || "Registration failed",
      status: response.status,
    };
  }

  setSession(data.token);
  navigate(goto);
  return { success: true };
};

export const isLoggedIn = () => !!getSession();

export const logout = (callback) => {
  window.localStorage.removeItem(SESSION_KEY);
  window.localStorage.removeItem(ACCOUNT_KEY);
  window.localStorage.removeItem("cart");
  if (callback) callback();
};
